import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend } from 'recharts';
import { Star, Anchor, Puzzle, AlertTriangle } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, PageHeader, QueryState, CALUM } from '../components/charts';
import DateRangeControl from '../components/DateRangeControl';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import { formatCurrency, formatCurrency2, formatNumber, formatDate } from '../lib/format';
import { todayISO } from '../lib/day';

/*
 * Menu engineering (Kasavana–Smith): every menu item plotted on populariteit
 * (verkochte stuks) × prijs per stuk (marge-proxy — er zijn geen kostprijzen in
 * de data). De kwadranten vertellen wat je met een item doet.
 */
const QUAD = {
  star: {
    label: 'Sterren', icon: Star, color: CALUM.gold,
    desc: 'Populair én hoge besteding — koesteren: prominent op de kaart, nooit schrappen.',
  },
  workhorse: {
    label: 'Werkpaarden', icon: Anchor, color: CALUM.green,
    desc: 'Veel verkocht, lagere prijs — het volume-fundament. Test voorzichtige prijsstappen of bundel met iets erbij.',
  },
  puzzle: {
    label: 'Puzzels', icon: Puzzle, color: '#a78bfa',
    desc: 'Hoge besteding maar weinig verkocht — beter presenteren, aanbevelen of anders op de kaart zetten.',
  },
  dog: {
    label: 'Honden', icon: AlertTriangle, color: '#94a3b8',
    desc: 'Weinig verkocht én lage besteding — kandidaten om te vervangen of te schrappen.',
  },
};

const ScatterTip = ({ active, payload }) => {
  const t = useT();
  if (!active || !payload?.length) return null;
  const it = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-0.5">{it.name}</p>
      <p className="text-slate-500">{formatNumber(it.units)} {t('stuks')} · {formatCurrency2(it.price)}/{t('stuk')}</p>
      <p className="text-slate-500">{t('omzet')} {formatCurrency(it.revenue)} ({String(it.revenue_share_pct).replace('.', ',')}%)</p>
    </div>
  );
};

export default function MenuAnalysis() {
  const t = useT();
  const [range, setRange] = useState({ preset: 'today', start_date: todayISO(), end_date: todayISO() });   // open on the current day
  const p = { start_date: range.start_date, end_date: range.end_date };
  const dr = useApi(() => api.dataRange(), []);
  const me = useApi(() => api.menuEngineering(p), [range.start_date, range.end_date]);
  return (
    <PageShell page="Menu-analyse" accent="gold" freshness={dr.data?.end ? `${t('Data t/m')} ${formatDate(dr.data.end)}` : ''}>
      <PageHeader title={t('Menu-analyse')}
        subtitle={t('Elk gerecht op populariteit × prijs — wat koester je, wat verbeter je, wat schrap je?')}>
        <DateRangeControl value={range} onChange={setRange} dataRange={dr.data} />
      </PageHeader>

      <QueryState q={me} isEmpty={(d) => !d.items?.length}>
        {(d) => { const items = d.items || []; return (
        <>
          <SectionCard title={t('Het menu in één beeld')}
            subtitle={`${d.item_count} ${t('producten met minstens')} ${d.min_units} ${t('verkochte stuks')} · ${t('populair vanaf')} ${formatNumber(d.pop_threshold)} ${t('stuks')} · ${t('hoge prijs vanaf')} ${formatCurrency2(d.price_threshold)}`}>
            <ResponsiveContainer width="100%" height={380}>
              <ScatterChart margin={{ left: 4, right: 16, top: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" dataKey="units" name={t('stuks')} scale="log" domain={['auto', 'auto']}
                  tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} />
                <YAxis type="number" dataKey="price" name={t('prijs')} tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `€${v}`} width={44} />
                <ReferenceLine x={d.pop_threshold} stroke="#94a3b8" strokeDasharray="4 3" />
                <ReferenceLine y={d.price_threshold} stroke="#94a3b8" strokeDasharray="4 3" />
                <Tooltip content={<ScatterTip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {Object.entries(QUAD).map(([q, cfg]) => (
                  <Scatter key={q} name={t(cfg.label)} data={items.filter((i) => i.quadrant === q)}
                    fill={cfg.color} fillOpacity={0.75} />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
            <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
              {t('Rechtsboven = sterren (veel verkocht, hoge prijs) · rechtsonder = werkpaarden (veel verkocht, lagere prijs) · linksboven = puzzels (weinig verkocht, hoge prijs) · linksonder = honden (weinig verkocht, lage prijs).')}
              {' '}
              {t('De horizontale as is samengedrukt, zodat kleine én grote verkopers leesbaar blijven.')}
              {' '}
              {t('Prijs per stuk geldt hier als schatting van de marge (kostprijzen zitten niet in de kassadata).')}
            </p>
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {Object.entries(QUAD).map(([q, cfg]) => {
              const rows = items.filter((i) => i.quadrant === q).sort((a, b) => b.revenue - a.revenue);
              const Icon = cfg.icon;
              return (
                <SectionCard key={q}
                  title={<span className="inline-flex items-center gap-1.5"><Icon className="h-4 w-4" style={{ color: cfg.color }} />{t(cfg.label)} ({rows.length})</span>}
                  subtitle={t(cfg.desc)}>
                  <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                      <th className="py-1">{t('Product')}</th><th className="text-right">{t('Stuks')}</th>
                      <th className="text-right">€/{t('stuk')}</th><th className="text-right">{t('Omzet')}</th>
                    </tr></thead>
                    <tbody>
                      {rows.slice(0, 8).map((i) => (
                        <tr key={i.name} className="border-t border-slate-100">
                          <td className="py-1"><span className="block truncate max-w-[170px]" title={i.name}>{i.name}</span></td>
                          <td className="text-right tabular-nums">{formatNumber(i.units)}</td>
                          <td className="text-right tabular-nums">{formatCurrency2(i.price)}</td>
                          <td className="text-right tabular-nums">{formatCurrency(i.revenue)}</td>
                        </tr>
                      ))}
                      {rows.length > 8 && (
                        <tr><td className="py-1 text-slate-400" colSpan={4}>… {t('en')} {rows.length - 8} {t('andere')}</td></tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </SectionCard>
              );
            })}
          </div>
        </>
        ); }}
      </QueryState>
    </PageShell>
  );
}
